import { Injectable } from '@angular/core';
import {
  PersonaConfig,
  PersonaPadlockError,
  RefreshAccessTokenConfig,
} from '@technologyfromsage/persona-padlock';
import { NEVER, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as URI from 'urijs';

import { AuthProvider } from '®shared/auth/auth-provider';
import { AuthServiceLogin } from '®shared/auth/auth-service-login';
import { AuthenticationFailedException } from '®shared/auth/authentication-failed-exception';
import {
  PersonaUser,
  createPersonaUserFromPkceLogin,
  isPersonaUser,
} from '®shared/auth/persona-user';
import { User } from '®shared/auth/user';
import { UserScopesService } from '®shared/auth/user-scopes.service';
import { AppConfig } from '®shared/config';
import { RedirectionService } from '®shared/redirection';

import { PersonaPadlockService } from './persona-padlock.service';

const USER_SSO_TIMEOUT_SECONDS = 5 * 60 * 1000;
const REDIRECT_PATH = '/3/redirect-authentication.html';

@Injectable()
export class PersonaPkceAuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  constructor(
    appConfig: AppConfig,
    private personaPadlockService: PersonaPadlockService,
    redirectionService: RedirectionService,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, redirectionService, userScopesService);
  }

  private handleError(error: PersonaPadlockError): Observable<never> {
    return throwError(
      new AuthenticationFailedException(
        (error && error.message) || 'Unknown authentication error',
      ),
    );
  }

  private getPersonaConfig(authProvider: string): PersonaConfig {
    return {
      clientId: this.appConfig.oauthAppId,
      personaOrigin: this.appConfig.personaOrigin,
      provider: authProvider,
      timeoutMilliseconds: USER_SSO_TIMEOUT_SECONDS,
    };
  }

  private getAuthProvider(currentUri: URI): string {
    return this.isTalisLoginUri(currentUri)
      ? this.appConfig.talisLoginProvider
      : this.appConfig.tenant.shortCode;
  }

  public obtainNewToken(returnUrl?: string): Observable<User> {
    const currentUri = returnUrl ? new URI(returnUrl) : this.getCurrentUri();
    const redirectUri = `${currentUri.origin()}${REDIRECT_PATH}`;

    const returnPathAndQuery = currentUri.resource();

    const appState = {
      target: returnPathAndQuery,
    };

    const authProvider = this.getAuthProvider(currentUri);
    const config = this.getPersonaConfig(authProvider);

    try {
      this.personaPadlockService.initiateRedirectAuthentication(
        config,
        redirectUri,
        appState,
      );

      return NEVER;
    } catch (error) {
      return this.handleError(error);
    }
  }

  public getCurrentToken(): Observable<User> {
    const currentUri = this.getCurrentUri();
    const authProvider = this.getAuthProvider(currentUri);
    const config = this.getPersonaConfig(authProvider);

    return this.personaPadlockService.silentlyAuthenticate(config).pipe(
      map(loginResponse => createPersonaUserFromPkceLogin(loginResponse)),
      map(personaUser => {
        if (!this.userScopesService.isAuthorizedAsMemberAtTenant(personaUser)) {
          throw new AuthenticationFailedException(
            'Persona User from another tenancy',
          );
        }
        return personaUser;
      }),
      catchError(this.handleError),
    );
  }

  public refreshToken(): Observable<User> {
    const user = this.currentUser;

    if (!isPersonaUser(user)) {
      return throwError(
        new AuthenticationFailedException('Not a persona user'),
      );
    }

    const refreshTokenConfig: RefreshAccessTokenConfig = {
      refreshToken: user.refreshToken,
      personaOrigin: this.appConfig.personaOrigin,
      userGuid: user.guid,
    };

    return this.personaPadlockService
      .refreshAccessToken(refreshTokenConfig)
      .pipe(
        map(
          loginResponse =>
            new PersonaUser({
              guid: user.guid,
              oauth: {
                access_token: loginResponse.access_token,
                refresh_token: loginResponse.refresh_token,
                scope: user.scopes,
                token_type: 'bearer',
              },
              profile: user.profile,
              active_pid: user.activePid,
            }),
        ),
        catchError(this.handleError),
      );
  }

  public login(): void {
    const returnUrl = this.getCurrentUri();
    returnUrl.removeQuery('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public loginDuringRouteNavigation(stateUrl: string): void {
    const urlBase = this.getCurrentUri().origin();
    const returnUrl = new URL(stateUrl, urlBase);
    returnUrl.searchParams.delete('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }
}
